(() => {
  if (window.baesicImageModeReady) return;
  window.baesicImageModeReady = true;
  // Placeholder state: an image counts as loaded only after it has really rendered a picture.
  const isVehicleImage = image => image?.matches?.('img[data-vehicle-image-key]');
  const markLoaded = image => { if (image.getAttribute('src') && image.complete && image.naturalWidth > 1) image.dataset.vehicleLoaded = 'true'; };
  const scanLoaded = () => document.querySelectorAll('img[data-vehicle-image-key]').forEach(markLoaded);
  document.addEventListener('load', event => { if (isVehicleImage(event.target)) markLoaded(event.target); }, true);
  document.addEventListener('error', event => { if (isVehicleImage(event.target)) delete event.target.dataset.vehicleLoaded; }, true);
  const storageKey = 'baesic-vehicle-image-mode';
  let mode = 'day';
  try { if (localStorage.getItem(storageKey) === 'night') mode = 'night'; } catch {}
  const states = new WeakMap();
  let mapping = {}, imageRatios = {}, revision = 0;
  let resolveData;
  const dataReady = new Promise(resolve => { resolveData = resolve; });
  const buttons = () => document.querySelectorAll('[data-vehicle-mode-toggle]');
  const syncButtons = () => buttons().forEach(button => {
    button.setAttribute('aria-checked', String(mode === 'night'));
    button.querySelector('[data-vehicle-mode-label]').textContent = button.dataset[mode];
  });
  const safeUrl = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, location.href);
      return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch { return null; }
  };
  const decode = async image => {
    let timeout;
    try {
      await Promise.race([image.decode?.(), new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Image decode timed out')), 8000); })]);
    } finally { clearTimeout(timeout); }
  };
  // Request Shopify CDN derivatives; retain original file references in the map.
  const responsiveSource = (image, source) => {
    const url = new URL(source, location.href);
    if (!(url.hostname === 'cdn.shopify.com' || (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'))) || /\.svg$/i.test(url.pathname)) return { src: source, srcset: null, sizes: null };
    const measured = image.getBoundingClientRect().width || image.closest('.image-wrapper')?.getBoundingClientRect().width || 320;
    const display = Math.ceil(measured * (image.closest('.collection-card, .chassis-card') ? 1.1 : 1));
    const candidates = [160, 320, 480, 640, 960, 1440, 1920, 2400];
    const limit = candidates.find(w => w >= display * Math.min(devicePixelRatio || 1, 2)) || 2400;
    const widths = candidates.filter(w => w <= limit);
    const atWidth = width => { const candidate = new URL(url); candidate.searchParams.set('width', width); return candidate.href; };
    return { src: atWidth(limit), srcset: widths.map(w => `${atWidth(w)} ${w}w`).join(', '), sizes: `${display}px` };
  };
  const applySource = (image, next) => {
    if (next.sizes) image.sizes = next.sizes;
    if (next.srcset) image.srcset = next.srcset;
    else image.removeAttribute('srcset');
    image.src = next.src;
  };
  const prepareImage = async (image, target, token, lazy = false) => {
    let state = states.get(image);
    if (!state) {
      state = { src: image.getAttribute('src') || image.dataset.vehicleInitialSrc, srcset: image.getAttribute('srcset') || image.dataset.vehicleInitialSrcset, sizes: image.getAttribute('sizes'), visibility: image.style.visibility, failed: new Set() };
      states.set(image, state);
    }
    const map = mapping[image.dataset.vehicleImageKey] || {};
    const day = safeUrl(map.day) || safeUrl(image.hasAttribute('data-vehicle-day-src') ? image.dataset.vehicleDaySrc : state.src);
    const night = safeUrl(map.night) || safeUrl(image.dataset.vehicleNightSrc);
    const sources = [...new Set([target === 'day' ? day : night, target === 'day' ? night : day, state.lastGood?.src].filter(Boolean))];
    let next = null;
    if (lazy && sources.length) {
      next = responsiveSource(image, sources[0]);
    } else for (const src of sources) {
      if (state.failed.has(src)) continue;
      if (token !== revision) return () => {};
      const candidate = responsiveSource(image, src);
      const preload = new Image();
      preload.fetchPriority = 'high';
      applySource(preload, candidate);
      try {
        await decode(preload);
        if (!preload.naturalWidth) throw new Error('Image unavailable');
        next = candidate;
        break;
      } catch { state.failed.add(src); }
    }
    return async () => {
      if (token !== revision || !image.isConnected) return;
      if (!next) {
        image.style.visibility = 'hidden';
        image.dataset.vehicleImageUnavailable = 'true';
        image.dataset.vehicleModeReady = target;
        return;
      }
      if (image.getAttribute('src') !== next.src || image.getAttribute('srcset') !== next.srcset) {
        const wrapper = image.closest('.model-header__image');
        if (wrapper) {
          wrapper.classList.remove('alpha-cropped');
          wrapper.querySelector('.model-header__cropped-canvas')?.remove();
          delete image.dataset.alphaProcessed;
        }
        applySource(image, next);
        if (!lazy) { try { await decode(image); } catch {} }
      }
      if (token !== revision || !image.isConnected) return;
      if (!lazy && image.naturalWidth) state.lastGood = next;
      image.style.visibility = state.visibility;
      markLoaded(image);
      delete image.dataset.vehicleImageUnavailable;
      image.dataset.vehicleModeReady = target;
    };
  };
  document.addEventListener('error', event => {
    const image = event.target;
    if (!image.matches?.('img[data-vehicle-image-key]')) return;
    const state = states.get(image);
    if (!state || state.recoveryRevision === revision) return;
    state.recoveryRevision = revision;
    if (image.getAttribute('srcset')) state.skipSrcset = true;
    else state.failed.add(safeUrl(image.currentSrc || image.src));
    prepareImage(image, mode, revision).then(apply => apply());
  }, true);
  const renderMode = async (animate = false) => {
    const token = ++revision, target = mode;
    syncButtons();
    buttons().forEach(button => button.setAttribute('aria-busy', 'true'));
    const scrollAtStart = { x: window.scrollX, y: window.scrollY };
    await dataReady;
    if (token !== revision) return;
    const images = [...document.querySelectorAll('img[data-vehicle-image-key]')];
    images.forEach(image => {
      const ratio = imageRatios[image.dataset.vehicleImageKey];
      if (Number.isFinite(ratio) && ratio > 0) image.closest('.image-wrapper')?.style.setProperty('--vehicle-image-ratio', String(ratio));
    });
    document.dispatchEvent(new CustomEvent('baesic:vehicle-ratios'));
    images.forEach(image => { const state = states.get(image); if (state) { state.failed.clear(); state.skipSrcset = false; } });
    const inViewport = image => { const r = image.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth; };
    let visible = images.map(inViewport);
    const plans = images.map((image, index) => prepareImage(image, target, token, image.loading === 'lazy' && !visible[index]));
    await Promise.all(plans.filter((_, index) => visible[index]));
    if (token !== revision) return;
    const stableViewport = scrollAtStart.x === window.scrollX && scrollAtStart.y === window.scrollY;
    if (!stableViewport) {
      visible = images.map(inViewport);
      await Promise.all(plans.filter((_, index) => visible[index]));
      if (token !== revision) return;
    }
    const commit = async () => {
      if (token !== revision) return;
      const applied = plans.map(plan => plan.then(apply => apply()));
      // Offscreen downloads do not delay the viewport reveal or flash cards into the wrong mode.
      Promise.all(applied).then(() => {
        if (token === revision) document.documentElement.dataset.vehicleModeReady = 'true';
      });
      await Promise.all(applied.filter((_, index) => visible[index]));
    };
    const transition = window.baesicSiteMode?.set(target, animate && stableViewport, commit);
    if (!transition) await commit();
    else await transition.finished.catch(() => {});
    if (token === revision) buttons().forEach(button => button.removeAttribute('aria-busy'));
  };
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-vehicle-mode-toggle]')) return;
    mode = mode === 'day' ? 'night' : 'day';
    try { localStorage.setItem(storageKey, mode); } catch {}
    renderMode(true);
  });
  window.addEventListener('storage', event => {
    if (event.key !== storageKey) return;
    mode = event.newValue === 'night' ? 'night' : 'day';
    renderMode();
  });
  let resizeTimer;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => renderMode(), 180); }, { passive: true });
  document.addEventListener('shopify:section:load', () => renderMode());
  document.addEventListener('baesic:vehicle-images-added', () => renderMode());
  const initialize = async () => {
    syncButtons();
    const element = document.querySelector('[data-vehicle-mode-map]');
    const read = async url => { if (!url) return null; try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
    const [map, dimensions] = await Promise.all([read(element?.dataset.vehicleModeMap), read(element?.dataset.vehicleModeDimensions)]);
    if (map?.version === 1 && map.images && typeof map.images === 'object') mapping = map.images;
    if (dimensions?.version === 1 && dimensions.ratios) imageRatios = dimensions.ratios;
    resolveData();
    scanLoaded();
    if (revision === 0) renderMode();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();

